import React, { useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  alpha,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PeopleIcon from "@mui/icons-material/People";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CategoryIcon from "@mui/icons-material/Category";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { MetricCard } from "../shared";
import InfoButton from "../Clinical/InfoButton";
import {
  TABLE_DEFAULTS,
  CONDITION_TYPE_COLORS,
  DISPOSITION_COLORS,
  PRIORITY_COLORS,
  formatNumber,
} from "../../constants/ceoDashboard";

// ── Helpers ──────────────────────────────────────────────────────

/** "DIABETES" -> "Diabetes" */
function sentenceCase(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/** Recapture rate color. */
function getRateColor(rate) {
  if (rate > 15) return "success";
  if (rate >= 5) return "warning";
  return "error";
}

// ── Component ────────────────────────────────────────────────────

/**
 * Panel 6 - Coding Revenue Opportunity (v2)
 * Stats cards + condition type chips + disposition chips +
 * Top HCCs table (with toggle details) + Recapture by PCP table (with toggle details).
 */
export default function PanelCodingRevenue({ data, loading, helpContent }) {
  const [showAllHCCs, setShowAllHCCs] = useState(false);
  const [showAllPCPs, setShowAllPCPs] = useState(false);
  const [showHCCDetails, setShowHCCDetails] = useState(false);
  const [showPCPDetails, setShowPCPDetails] = useState(false);

  // ── Loading state ────────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={3}>
          {/* Header skeleton */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Skeleton variant="rounded" width={36} height={36} animation="wave" />
            <Skeleton variant="text" width={280} height={32} animation="wave" />
          </Stack>

          {/* Metric cards skeleton */}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <MetricCard key={i} loading />
            ))}
          </Box>

          {/* Tables skeleton */}
          <Box
            sx={{
              display: "grid",
              gap: 2.5,
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            }}
          >
            <Skeleton
              variant="rectangular"
              height={280}
              animation="wave"
              sx={{ borderRadius: 3 }}
            />
            <Skeleton
              variant="rectangular"
              height={280}
              animation="wave"
              sx={{ borderRadius: 3 }}
            />
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (!data) return null;

  // ── Data slices ──────────────────────────────────────────────
  const summary = data.summary || {};
  const hccs = data.topHCCs?.topHCCs || [];
  const pcps = data.recaptureByPCP?.recaptureByPCP || [];
  const conditionTypes = summary.conditionTypeBreakdown || {};
  const dispositions = summary.dispositionBreakdown || {};

  const visibleHCCs = showAllHCCs ? hccs : hccs.slice(0, TABLE_DEFAULTS.topHCCs);
  const visiblePCPs = showAllPCPs ? pcps : pcps.slice(0, TABLE_DEFAULTS.recaptureByPCP);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          background: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
          opacity: 0.06,
          borderRadius: "50%",
          filter: "blur(40px)",
        }}
      />

      <Stack spacing={3} sx={{ position: "relative" }}>
        {/* ── Header ──────────────────────────────────────────── */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "secondary.main",
              color: "white",
            }}
          >
            <CodeIcon fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            Coding Revenue Opportunity
          </Typography>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* ── 4 Metric Cards ──────────────────────────────────── */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
          }}
        >
          <MetricCard
            title="Total Opportunities"
            value={formatNumber(summary.totalOpportunities)}
            icon={<AssignmentIcon />}
            color="primary"
          />
          <MetricCard
            title="Unique Patients"
            value={formatNumber(summary.uniquePatients)}
            icon={<PeopleIcon />}
            color="info"
          />
          <MetricCard
            title="Not Addressed"
            value={`${summary.notAddressedPct}%`}
            subtitle={`${formatNumber(summary.notAddressedCount)} opportunities`}
            icon={<ErrorOutlineIcon />}
            color="error"
          />
          <MetricCard
            title="HCC Categories"
            value={formatNumber(summary.hccCategories)}
            icon={<CategoryIcon />}
            color="secondary"
          />
        </Box>

        {/* ── Condition Type Chips ────────────────────────────── */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {Object.entries(conditionTypes).map(([type, count]) => {
            const colors = CONDITION_TYPE_COLORS[type];
            if (!colors) return null;
            return (
              <Chip
                key={type}
                label={`${type}: ${formatNumber(count)}`}
                sx={{
                  bgcolor: colors.bg,
                  color: colors.text,
                  fontWeight: 700,
                  fontSize: "0.8rem",
                }}
              />
            );
          })}
        </Stack>

        {/* ── Disposition Breakdown Chips ─────────────────────── */}
        {Object.keys(dispositions).length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(dispositions).map(([disposition, count]) => {
              const colors = DISPOSITION_COLORS[disposition] || {
                bg: "#F3F4F6",
                text: "#374151",
              };
              return (
                <Chip
                  key={disposition}
                  label={`${disposition}: ${formatNumber(count)}`}
                  size="small"
                  sx={{
                    bgcolor: colors.bg,
                    color: colors.text,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
              );
            })}
          </Stack>
        )}

        {/* ── 2-column tables ─────────────────────────────────── */}
        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          }}
        >
          {/* LEFT: Top HCCs Not Addressed */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Top HCCs Not Addressed
              </Typography>
              <Button
                size="small"
                startIcon={
                  showHCCDetails ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )
                }
                onClick={() => setShowHCCDetails((prev) => !prev)}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
              >
                {showHCCDetails ? "Hide details" : "Show details"}
              </Button>
            </Stack>

            <Box sx={{ overflowX: "auto", flex: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                      HCC Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Description
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      Count
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      Patients
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      Priority
                    </TableCell>
                    {showHCCDetails && (
                      <>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Last Action
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Observation
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleHCCs.map((hcc) => {
                    const priorityColors =
                      PRIORITY_COLORS[hcc.priority?.toUpperCase()] || null;
                    return (
                      <TableRow
                        key={hcc.hccId}
                        sx={{
                          "&:hover": { bgcolor: "action.hover" },
                          transition: "background-color 0.2s",
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={hcc.hccId}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: alpha("#8B5CF6", 0.1),
                              color: "#6D28D9",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                            {sentenceCase(hcc.hccDescription)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {formatNumber(hcc.count)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={500}>
                            {formatNumber(hcc.uniquePatients)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {priorityColors ? (
                            <Chip
                              label={sentenceCase(hcc.priority)}
                              size="small"
                              sx={{
                                bgcolor: priorityColors.bg,
                                color: priorityColors.text,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {hcc.priority ? sentenceCase(hcc.priority) : "-"}
                            </Typography>
                          )}
                        </TableCell>
                        {showHCCDetails && (
                          <>
                            <TableCell>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 140 }}
                              >
                                {hcc.lastAction || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 160 }}
                              >
                                {hcc.observation || "-"}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {hccs.length > TABLE_DEFAULTS.topHCCs && (
              <Box sx={{ textAlign: "center", mt: 1.5 }}>
                <Button
                  size="small"
                  onClick={() => setShowAllHCCs((prev) => !prev)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {showAllHCCs ? "Show less" : `Show all ${hccs.length}`}
                </Button>
              </Box>
            )}
          </Paper>

          {/* RIGHT: Recapture Rate by PCP */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Recapture Rate by PCP
              </Typography>
              <Button
                size="small"
                startIcon={
                  showPCPDetails ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )
                }
                onClick={() => setShowPCPDetails((prev) => !prev)}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
              >
                {showPCPDetails ? "Hide details" : "Show details"}
              </Button>
            </Stack>

            <Box sx={{ overflowX: "auto", flex: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                      PCP Name
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      Addressed
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 600, color: "text.secondary", minWidth: 120 }}
                    >
                      Rate
                    </TableCell>
                    {showPCPDetails && (
                      <>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Last Action
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Observation
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePCPs.map((pcp) => {
                    const rateColor = getRateColor(pcp.recaptureRate);
                    return (
                      <TableRow
                        key={pcp.pcpName}
                        sx={{
                          "&:hover": { bgcolor: "action.hover" },
                          transition: "background-color 0.2s",
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{ maxWidth: 180 }}
                          >
                            {pcp.pcpName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={500}>
                            {formatNumber(pcp.totalOpportunities)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={500}>
                            {formatNumber(pcp.addressed)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack spacing={0.5} alignItems="center">
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(pcp.recaptureRate, 100)}
                              color={rateColor}
                              sx={{
                                width: "100%",
                                height: 8,
                                borderRadius: 4,
                                bgcolor: (theme) =>
                                  alpha(theme.palette[rateColor].main, 0.12),
                              }}
                            />
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color={`${rateColor}.main`}
                            >
                              {pcp.recaptureRate}%
                            </Typography>
                          </Stack>
                        </TableCell>
                        {showPCPDetails && (
                          <>
                            <TableCell>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 140 }}
                              >
                                {pcp.lastAction || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 160 }}
                              >
                                {pcp.observation || "-"}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {pcps.length > TABLE_DEFAULTS.recaptureByPCP && (
              <Box sx={{ textAlign: "center", mt: 1.5 }}>
                <Button
                  size="small"
                  onClick={() => setShowAllPCPs((prev) => !prev)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {showAllPCPs ? "Show less" : `Show all ${pcps.length}`}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Stack>
    </Paper>
  );
}
